<template>
  <hb-section :title="$t('influencer_panel.tab_withdrawals')" :subtitle="summary">
    <template #actions>
      <b-button variant="primary" size="sm" @click="$emit('request')">
        {{ $t('influencer_panel.request_withdrawal') }}
      </b-button>
    </template>

    <hb-empty-state
      v-if="!withdrawals.length"
      :title="$t('influencer_panel.no_withdrawals')"
      icon="CreditCardIcon"
    />

    <div v-else>
      <div v-for="item in withdrawals" :key="item.id" class="withdrawals__row">
        <div class="withdrawals__day">
          <div class="withdrawals__day-number">{{ dayChip(item.requested_at).day }}</div>
          <div class="withdrawals__day-month">{{ dayChip(item.requested_at).month }}</div>
        </div>

        <div class="withdrawals__identity">
          <hb-money :cents="item.amount_cents" />
          <div class="withdrawals__destination">{{ destination(item) }}</div>
          <div v-if="item.rejection_reason || item.failure_reason" class="withdrawals__reason">
            {{ item.rejection_reason || item.failure_reason }}
          </div>
        </div>

        <div class="withdrawals__status">
          <hb-status-badge :status="item.status" domain="withdrawal" />
          <div v-if="item.scheduled_for" class="withdrawals__scheduled">
            {{ $t('influencer_panel.scheduled_for') }}: {{ shortDate(item.scheduled_for) }}
          </div>
          <div v-if="item.external_reference" class="withdrawals__e2e">E2E {{ item.external_reference }}</div>
        </div>

        <div class="withdrawals__actions">
          <b-button
            v-if="item.has_receipt"
            variant="flat-primary"
            size="sm"
            @click="$emit('receipt', item)"
          >
            <feather-icon icon="DownloadIcon" size="14" class="mr-50" />
            {{ $t('influencer_panel.receipt') }}
          </b-button>
          <b-button
            v-if="item.status === 'SO'"
            variant="outline-danger"
            size="sm"
            @click="$emit('cancel', item)"
          >
            {{ $t('action.cancel') }}
          </b-button>
        </div>
      </div>

      <p class="withdrawals__note">
        <feather-icon icon="InfoIcon" size="13" />
        {{ $t('influencer_panel.withdraw_hint') }}
      </p>
    </div>
  </hb-section>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import HbSection from '@/components/hubee/HbSection.vue'
import HbMoney from '@/components/hubee/HbMoney.vue'
import HbStatusBadge from '@/components/hubee/HbStatusBadge.vue'
import HbEmptyState from '@/components/hubee/HbEmptyState.vue'
import { dayChip, shortDate } from '../panel'

/**
 * Historico de saques.
 *
 * O design mostrava banco e conta no destino. Nao ha instituicao financeira ligada ao
 * saque no modelo — o que fica guardado e uma copia da chave PIX no momento do pedido,
 * entao o destino aqui e o tipo da chave mais a chave mascarada.
 */
const PIX_TYPE_LABELS = {
  CP: 'CPF', CN: 'CNPJ', EM: 'E-mail', TE: 'Telefone', AL: 'Aleatória',
}

export default {
  name: 'WithdrawalsPane',
  components: {
    BButton, HbSection, HbMoney, HbStatusBadge, HbEmptyState,
  },
  props: {
    withdrawals: { type: Array, default: () => [] },
  },
  computed: {
    summary() {
      const paid = this.withdrawals.filter(w => w.status === 'PG')
      const total = paid.reduce((sum, w) => sum + Number(w.amount_cents || 0), 0)

      return this.$t('influencer_panel.withdrawals_summary', {
        count: paid.length,
        total: (total / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      })
    },
  },
  methods: {
    dayChip,
    shortDate,
    destination(item) {
      const type = PIX_TYPE_LABELS[item.pix_key_type] || this.$t('influencer_panel.pix_key')

      return item.masked_pix_key ? `${type} · ${item.masked_pix_key}` : type
    },
  },
}
</script>

<style scoped>
.withdrawals__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.85rem;
  padding: 0.8rem 0.5rem;
  border-bottom: 1px solid var(--border-color);
}

.withdrawals__row:last-of-type {
  border-bottom: none;
}

.withdrawals__day {
  flex: none;
  width: 44px;
  text-align: center;
  line-height: 1.1;
}

.withdrawals__day-number {
  font-size: 1.14rem;
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.withdrawals__day-month {
  font-size: var(--fs-xs);
  color: var(--text-muted);
}

.withdrawals__identity {
  flex: 1 1 200px;
  min-width: 0;
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.withdrawals__destination {
  font-size: var(--fs-sm);
  font-weight: var(--fw-normal);
  color: var(--text-muted);
}

.withdrawals__reason {
  font-size: var(--fs-sm);
  font-weight: var(--fw-normal);
  color: var(--danger);
}

.withdrawals__status {
  flex: 0 0 auto;
}

.withdrawals__scheduled,
.withdrawals__e2e {
  margin-top: 0.2rem;
  font-size: var(--fs-xs);
  color: var(--text-muted);
}

.withdrawals__actions {
  flex: 0 0 auto;
  margin-left: auto;
  display: flex;
  gap: 0.35rem;
}

.withdrawals__note {
  margin: 1rem 0 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: var(--fs-xs);
  color: var(--text-muted);
}
</style>
